import { useUserRole } from '../context/UserRoleContext'
import { useDarkMode } from '../context/DarkModeContext'
import { useBrand } from './useBrand'
import { getRoleClasses } from '../utils/roleTheme'

/**
 * useRoleTheme
 *
 * Returns a set of Tailwind class strings derived from the current user role.
 * Brands with a fixed palette return their tokens from data/brandThemes.ts.
 */
/**
 * Dark-mode replacements for the light-only role tokens. Written as literal
 * strings (not templates) so Tailwind's content scanner generates them.
 */
const DARK_ROLE_OVERRIDES = {
  reader: {
    secondaryButton:
      'bg-indigo-900/40 text-indigo-300 hover:bg-indigo-900/60',
    outlineButton: 'border-indigo-400 text-indigo-300 hover:bg-indigo-900/30',
    activeTab: 'bg-indigo-900/40 text-indigo-300',
    inactiveTab: 'text-gray-300 hover:bg-gray-700',
    link: 'text-indigo-400 hover:text-indigo-300',
    badge: 'bg-indigo-900/40 text-indigo-300',
    textPrimary: 'text-indigo-400',
    textHover: 'hover:text-indigo-300',
    bgLight: 'bg-indigo-900/20',
    bgMedium: 'bg-indigo-900/40',
  },
  vendor: {
    secondaryButton:
      'bg-purple-900/40 text-purple-300 hover:bg-purple-900/60',
    outlineButton: 'border-purple-400 text-purple-300 hover:bg-purple-900/30',
    activeTab: 'bg-purple-900/40 text-purple-300',
    inactiveTab: 'text-gray-300 hover:bg-gray-700',
    link: 'text-purple-400 hover:text-purple-300',
    badge: 'bg-purple-900/40 text-purple-300',
    textPrimary: 'text-purple-400',
    textHover: 'hover:text-purple-300',
    bgLight: 'bg-purple-900/20',
    bgMedium: 'bg-purple-900/40',
  },
} as const

export const useRoleTheme = () => {
  const { isReader } = useUserRole()
  const { theme } = useBrand()
  const { darkMode } = useDarkMode()

  // Brands with a fixed accent palette (AfterTide: gold + pink) ignore role
  // and dark mode. The tokens live in data/brandThemes.ts.
  if (theme.fixedRoleTokens) {
    return {
      ...theme.fixedRoleTokens,
      badgeRadius: theme.tagRadius,
      isReader,
      role: isReader ? ('reader' as const) : ('vendor' as const),
    }
  }

  const role = isReader ? 'reader' : 'vendor'
  // getRoleClasses is light-only: 50/100 tints, 600-level text and a
  // gray-700 inactive tab. In dark mode those render as bright panels behind
  // light body text, or near-invisible grey on a dark page. The dark values
  // below replace ONLY those tokens; light mode is untouched.
  const classes = darkMode
    ? { ...getRoleClasses(role), ...DARK_ROLE_OVERRIDES[role] }
    : getRoleClasses(role)

  return {
    // ─── Buttons ────────────────────────────────────────────────────────────
    /** Full primary button: bg + hover + text-white + focus ring */
    primaryButton: classes.primaryButton,
    /** Secondary/ghost button: light bg + colored text + hover */
    secondaryButton: classes.secondaryButton,
    /** Outline button: colored border + text + hover bg */
    outlineButton: classes.outlineButton,

    // ─── Form elements ───────────────────────────────────────────────────────
    /** Input focus ring + border */
    inputFocus: classes.input,
    /** Checkbox color + focus ring */
    checkbox: classes.checkbox,
    /** Toggle checked bg + focus ring */
    toggle: classes.toggle,

    // ─── Navigation / Tabs ──────────────────────────────────────────────────
    /** Active tab: light bg + colored text */
    activeTab: classes.activeTab,
    /** Inactive tab: gray text + hover */
    inactiveTab: classes.inactiveTab,

    // ─── Links ──────────────────────────────────────────────────────────────
    /** Colored link text + hover */
    link: classes.link,

    // ─── Badges / Tags ──────────────────────────────────────────────────────
    /** Light bg + colored text badge */
    badge: classes.badge,

    // ─── Borders ────────────────────────────────────────────────────────────
    /** Active/selected border color */
    borderActive: classes.borderActive,
    /** Focus border color */
    borderFocus: classes.borderFocus,

    // ─── Text ───────────────────────────────────────────────────────────────
    /** Primary accent text color */
    textPrimary: classes.textPrimary,
    /** Hover text color */
    textHover: classes.textHover,

    // ─── Backgrounds ────────────────────────────────────────────────────────
    /** Very light tinted bg (50) */
    bgLight: classes.bgLight,
    /** Light tinted bg (100) */
    bgMedium: classes.bgMedium,
    /** Full accent bg (500/600) */
    bgDark: classes.bgDark,

    // ─── Convenience combos ─────────────────────────────────────────────────
    accentLink: `${classes.textPrimary} ${classes.textHover}`,
    voteButton: classes.secondaryButton,
    badgeBg: classes.bgDark,

    // ─── Secondary accent ────────────────────────────────────────────────────
    /** Secondary accent text (Reader: purple, Vendor: indigo) */
    accentSecondaryText: isReader ? 'text-purple-500' : 'text-indigo-500',
    /** Secondary accent solid background */
    accentSecondaryBg: isReader ? 'bg-purple-500' : 'bg-indigo-500',
    /** Secondary accent hover background */
    accentSecondaryHover: isReader
      ? 'hover:bg-purple-600'
      : 'hover:bg-indigo-600',
    /** Secondary accent tinted background */
    accentSecondaryTint: darkMode
      ? isReader
        ? 'bg-purple-900/20'
        : 'bg-indigo-900/20'
      : isReader
        ? 'bg-purple-50'
        : 'bg-indigo-50',
    /** Secondary accent tinted background (darker) */
    accentSecondaryTintHover: darkMode
      ? isReader
        ? 'bg-purple-900/40'
        : 'bg-indigo-900/40'
      : isReader
        ? 'bg-purple-100'
        : 'bg-indigo-100',
    /** Secondary accent border */
    accentSecondaryBorder: darkMode
      ? isReader
        ? 'border-purple-700'
        : 'border-indigo-700'
      : isReader
        ? 'border-purple-200'
        : 'border-indigo-200',
    /** Full secondary action button */
    secondaryActionButton: isReader
      ? 'bg-purple-500 hover:bg-purple-600 text-white font-medium'
      : 'bg-indigo-500 hover:bg-indigo-600 text-white font-medium',

    // ─── Badge shape ─────────────────────────────────────────────────────────
    /** Badge border-radius, from the brand theme */
    badgeRadius: theme.tagRadius,

    // ─── Raw role info ───────────────────────────────────────────────────────
    isReader,
    role,
  }
}

export default useRoleTheme
